import { useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Award, Languages, Music2, Users, Youtube } from "lucide-react";
import { SITE } from "@/config/site";
import { Reveal } from "@/components/Reveal";
import { StarOrnament, StarPattern } from "@/components/Ornament";
import { BookButton } from "@/components/GoldButton";
import { AskChittiButton } from "@/components/AskChittiButton";
import { StickyCta } from "@/components/StickyCta";
import { trackEvent, trackOutbound, withUtm } from "@/lib/analytics";

const EASE = [0.16, 1, 0.3, 1];

// ============================================================================
// USHA MANGESHKAR — dedicated profile page (Chief Guest).
//
// Mirrors the storytelling architecture of the Kaleem Pasha /artist page
// (pages/Artist.jsx): same shell, same shared components (ChapterHeading-style
// numbered sections, BookButton, AskChittiButton, Reveal, StarOrnament/
// StarPattern), same restrained "premium tribute" visual language. It does
// NOT touch Artist.jsx itself.
//
// Every fact rendered here comes from SITE.ushaMangeshkar / SITE.chiefGuest
// in config/site.js, which in turn trace to the project's approved research
// dossier. See that config's comments for exactly what was deliberately left
// out (disputed birthplace/date, unverified training claims, etc.) and why.
//
// PHOTO: no approved photograph of Usha Mangeshkar exists yet. This page
// reads SITE.chiefGuest.hasPhoto/photo (the same flags the homepage Chapter 04
// section uses) so both switch to a real portrait together, automatically,
// the moment one is supplied — nothing here should be edited to make that
// switch. Until then, the Hero uses the same ornamental medallion treatment
// as the homepage, and "With the Legends" uses the same honest star/arch
// placeholder already used on the Artist page for unverified-photo entries.
//
// VIDEO: no individual YouTube video could be manually verified against an
// official/authorized channel in this research pass, so no video is embedded
// here — only a link to the channel that explicitly self-identifies as her
// official artist channel. See SITE.ushaMangeshkar.videos.
// ============================================================================

function MaskedLine({ children, delay = 0, className = "" }) {
  const reduce = useReducedMotion();
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${className}`}
        initial={reduce ? false : { y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

function SectionHeading({ number, title, icon: Icon }) {
  return (
    <Reveal>
      <div className="flex items-center gap-4">
        {Icon && <Icon className="h-6 w-6 shrink-0 text-[#D4AF37]" aria-hidden="true" />}
        <span className="font-display text-lg text-[#D4AF37]">{number}</span>
        <span className="h-px flex-1 bg-gradient-to-r from-[#D4AF37]/60 to-transparent" aria-hidden="true" />
      </div>
      <h2 className="font-display mt-5 text-3xl leading-tight tracking-tight text-[#F7F4EF] sm:text-4xl lg:text-5xl">
        {title}
      </h2>
    </Reveal>
  );
}

export default function UshaMangeshkarPage() {
  const guest = SITE.chiefGuest;
  const usha = SITE.ushaMangeshkar;
  const initials = guest.name
    .split(" ")
    .map((w) => w[0])
    .join("");

  useEffect(() => {
    trackEvent("page_view", { page_path: "/usha-mangeshkar" });
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#080705] text-[#F7F4EF] antialiased">
      <div className="grain" aria-hidden="true" />

      {/* Header */}
      <header className="relative z-20 border-b border-[#D4AF37]/15">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3 sm:px-8">
          <a href="/" data-testid="usha-home-link" className="flex items-center">
            <img src={SITE.brand.logoSmall} alt={SITE.brand.logoAlt} className="h-12 w-auto" />
          </a>
          <a
            href="/"
            data-testid="usha-back-link"
            className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/30 px-5 py-2 text-xs font-semibold uppercase tracking-[0.12em] text-[#F3E5AB] transition-colors hover:bg-[#D4AF37]/10"
          >
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
            Back to Site
          </a>
        </div>
      </header>

      <main>
        {/* SECTION 1 — HERO */}
        <section
          data-testid="usha-hero-section"
          className="relative overflow-hidden px-5 py-16 sm:px-8 sm:py-24 lg:py-28"
        >
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            <div className="absolute -top-40 left-1/2 h-[560px] w-[900px] -translate-x-1/2 rounded-full bg-[#D4AF37]/[0.09] blur-[120px]" />
            <div className="absolute bottom-0 right-0 h-[320px] w-[420px] rounded-full bg-[#7A1B2B]/[0.07] blur-[110px]" />
          </div>

          <div className="relative mx-auto grid max-w-6xl grid-cols-1 items-center gap-14 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="text-xs font-bold uppercase tracking-[0.28em] text-[#D4AF37]">
                {guest.role} at {SITE.eventName}
              </p>
              <h1 className="font-display mt-4 text-[13vw] leading-[0.98] tracking-tight text-[#F7F4EF] sm:text-6xl lg:text-7xl">
                <MaskedLine delay={0.1} className="gold-text">
                  {guest.name}
                </MaskedLine>
              </h1>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.4, ease: EASE }}
                className="font-accent mt-6 max-w-xl text-xl italic leading-relaxed text-[#C8C2B7] sm:text-2xl"
              >
                {guest.presenceLine}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.52, ease: EASE }}
                className="mt-6 flex flex-wrap items-center gap-3"
              >
                <span className="rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/[0.08] px-5 py-2 text-xs font-bold uppercase tracking-[0.2em] text-[#F3E5AB] sm:text-sm">
                  {guest.role}
                </span>
                {guest.performanceNote && (
                  <span
                    data-testid="usha-hero-performance-note"
                    className="rounded-full border border-[#D4AF37]/25 bg-[#D4AF37]/[0.04] px-5 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-[#D4AF37]/90 sm:text-sm"
                  >
                    {guest.performanceNote}
                  </span>
                )}
              </motion.div>

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.64, ease: EASE }}
                className="mt-8 max-w-xl text-base leading-[1.85] text-[#C8C2B7] sm:text-lg"
              >
                A legendary voice of Indian playback singing joins the mehfil — a chapter in her decades-long
                career, and a special presence in ours.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.76, ease: EASE }}
                className="mt-9 flex flex-wrap items-center gap-4"
              >
                <BookButton location="usha_hero" size="lg" testId="usha-hero-book-tickets-btn" />
                <AskChittiButton location="usha_hero" testId="usha-hero-ask-chitti-btn" />
              </motion.div>
            </div>

            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, delay: 0.3, ease: EASE }}
                className="relative mx-auto"
              >
                {guest.hasPhoto && guest.photo ? (
                  <div className="relative mx-auto w-full max-w-[380px]">
                    <div className="cah-light-sweep arch-frame relative aspect-[3/4.2] w-full overflow-hidden border border-[#D4AF37]/35 bg-[#0E0C0A]">
                      <img
                        src={guest.photo}
                        alt={`${guest.name} — Chief Guest`}
                        data-testid="usha-hero-photo"
                        className="cah-kenburns absolute inset-0 h-full w-full object-cover"
                        loading="eager"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                      <div className="pointer-events-none absolute inset-[8px] border border-[#F3E5AB]/15" aria-hidden="true" />
                    </div>
                    <div
                      className="arch-frame absolute -left-4 top-4 -z-10 h-full w-full border border-[#D4AF37]/15"
                      aria-hidden="true"
                    />
                  </div>
                ) : (
                  // Ornamental seal/medallion — same royal-invitation motif used on
                  // the homepage Chief Guest section, standing in for a portrait
                  // until an approved photograph is supplied.
                  <div className="relative mx-auto flex h-64 w-64 items-center justify-center rounded-full border-2 border-dashed border-[#D4AF37]/35 sm:h-72 sm:w-72">
                    <div className="cah-light-sweep relative flex h-[84%] w-[84%] items-center justify-center overflow-hidden rounded-full border border-[#D4AF37]/50 bg-gradient-to-b from-[#171310] to-[#0A0805] shadow-[0_0_60px_-12px_rgba(212,175,55,0.28)]">
                      <StarPattern id="usha-hero-medallion-pattern" opacity={0.2} className="rounded-full" />
                      <div
                        className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_50%_32%,rgba(212,175,55,0.18),transparent_70%)]"
                        aria-hidden="true"
                      />
                      <div className="pointer-events-none absolute inset-[6px] rounded-full border border-[#F3E5AB]/15" aria-hidden="true" />
                      <div className="relative flex flex-col items-center gap-2 px-4 text-center">
                        <span
                          className="h-px w-8 bg-gradient-to-r from-transparent via-[#D4AF37]/70 to-transparent"
                          aria-hidden="true"
                        />
                        <span
                          data-testid="usha-hero-monogram"
                          className="font-display gold-text text-5xl tracking-[0.06em] sm:text-6xl"
                        >
                          {initials}
                        </span>
                        <span
                          className="h-px w-8 bg-gradient-to-r from-transparent via-[#D4AF37]/70 to-transparent"
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          </div>
        </section>

        {/* SECTION 2 — HER JOURNEY */}
        <section data-testid="usha-journey-section" className="relative px-5 py-20 sm:px-8 sm:py-28">
          <div className="mx-auto max-w-4xl">
            <SectionHeading number="01" title="Her Journey" />
            <Reveal delay={0.12}>
              <p className="mt-8 text-base leading-[1.85] text-[#C8C2B7] sm:text-lg">{usha.intro}</p>
            </Reveal>
          </div>
        </section>

        {/* SECTION 3 — A VOICE ACROSS LANGUAGES */}
        <section data-testid="usha-languages-section" className="relative px-5 py-20 sm:px-8 sm:py-28">
          <div className="mx-auto max-w-6xl">
            <SectionHeading number="02" title="A Voice Across Languages" icon={Languages} />
            <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-3">
              {usha.languages.items.map((l, i) => (
                <Reveal key={l.name} delay={i * 0.08}>
                  <div className="h-full rounded-3xl border border-[#D4AF37]/20 bg-[#161310] p-7">
                    <StarOrnament className="h-6 w-6 text-[#D4AF37]/70" />
                    <h3 className="font-display mt-4 text-xl text-[#F7F4EF]">{l.name}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-[#C8C2B7] sm:text-base">{l.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.3}>
              <div className="mt-6 rounded-3xl border border-dashed border-[#D4AF37]/25 p-7">
                <p className="font-accent text-base italic leading-relaxed text-[#C8C2B7] sm:text-lg">
                  {usha.languages.note}
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        {/* SECTION 4 — SIGNATURE SONGS */}
        <section data-testid="usha-songs-section" className="relative px-5 py-20 sm:px-8 sm:py-28">
          <div className="mx-auto max-w-6xl">
            <SectionHeading number="03" title="Signature Songs" icon={Music2} />
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-2xl text-sm leading-relaxed text-[#918A7E] sm:text-base">
                Eight songs across a decades-long career — listen on legitimate platforms; nothing here is hosted or
                downloaded on this site.
              </p>
            </Reveal>
            <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {usha.songs.map((s, i) => (
                <Reveal key={s.title} delay={0.08 + i * 0.06}>
                  <div className="flex h-full flex-col rounded-3xl border border-[#D4AF37]/20 bg-[#161310] p-7">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#D4AF37]">
                      {s.film} · {s.year}
                    </p>
                    <h3 className="font-display mt-3 text-xl leading-snug text-[#F7F4EF]">{s.title}</h3>
                    <p className="mt-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#F3E5AB]/80">
                      {s.language}
                      {s.coSinger ? ` · ${s.coSinger}` : ""}
                    </p>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-[#C8C2B7]">{s.note}</p>
                    {s.link ? (
                      <a
                        href={s.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`usha-song-listen-${i}`}
                        onClick={() => {
                          trackEvent("performer_song_link_click", { performer: guest.name, song_title: s.title });
                          trackOutbound("usha-song-listen", { song_title: s.title });
                        }}
                        className="mt-5 inline-flex items-center gap-1.5 self-start text-xs font-semibold uppercase tracking-[0.14em] text-[#D4AF37] underline decoration-[#D4AF37]/30 underline-offset-4 transition-colors hover:text-[#E6CA65]"
                      >
                        {s.linkLabel || "Listen"}
                        <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                      </a>
                    ) : (
                      <p className="mt-5 text-xs italic text-[#6B655B]">
                        A verified streaming link isn&rsquo;t available yet for this recording.
                      </p>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 5 — MUSIC & VIDEO */}
        <section data-testid="usha-video-section" className="relative px-5 py-20 sm:px-8 sm:py-28">
          <div className="mx-auto max-w-4xl">
            <SectionHeading number="04" title="Music &amp; Video" icon={Youtube} />
            <Reveal delay={0.12}>
              <div className="mt-10 rounded-3xl border border-[#D4AF37]/20 bg-[#161310] p-8 sm:p-10">
                <div className="flex flex-wrap items-center gap-3">
                  <Youtube className="h-5 w-5 shrink-0 text-[#D4AF37]" aria-hidden="true" />
                  <h3 className="font-display text-xl text-[#F7F4EF] sm:text-2xl">{usha.videos.channelName}</h3>
                  <span className="rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-[#F3E5AB]">
                    {usha.videos.channelLabel}
                  </span>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-[#C8C2B7] sm:text-base">{usha.videos.note}</p>
                <a
                  href={withUtm(usha.videos.channelUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="usha-youtube-channel-link"
                  onClick={() => {
                    trackEvent("performer_youtube_click", { performer: guest.name, cta_location: "usha_video_section" });
                    trackOutbound("performer-youtube", { performer: guest.name });
                  }}
                  className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#918A7E] underline decoration-[#D4AF37]/30 underline-offset-4 transition-colors hover:text-[#F3E5AB]"
                >
                  Visit Her YouTube Channel
                  <ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {/* SECTION 6 — WITH THE LEGENDS */}
        <section data-testid="usha-legends-section" className="relative px-5 py-20 sm:px-8 sm:py-28">
          <div className="mx-auto max-w-6xl">
            <SectionHeading number="05" title="With the Legends" icon={Users} />
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-2xl text-base leading-[1.8] text-[#C8C2B7] sm:text-lg">{usha.legends.intro}</p>
            </Reveal>
            <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {usha.legends.items.map((p, i) => (
                <Reveal key={p.name} delay={0.1 + i * 0.05}>
                  <div
                    data-testid={`usha-legend-card-${i + 1}`}
                    className="arch-frame group relative aspect-[3/4.2] w-full overflow-hidden border border-[#D4AF37]/25 bg-[#161310] transition-colors duration-500 hover:border-[#D4AF37]/55"
                  >
                    {p.photo ? (
                      <>
                        <img
                          src={p.photo}
                          alt={p.name}
                          data-testid={`usha-legend-photo-${i + 1}`}
                          loading="lazy"
                          className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                        />
                        <div
                          className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(8,7,5,0)_38%,rgba(8,7,5,0.55)_70%,rgba(8,7,5,0.92)_100%)]"
                          aria-hidden="true"
                        />
                      </>
                    ) : (
                      <>
                        <StarPattern id={`usha-legend-pattern-${i}`} opacity={0.22} />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <StarOrnament className="h-7 w-7 text-[#D4AF37]/45" />
                        </div>
                        <div
                          className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-[linear-gradient(180deg,transparent_0%,rgba(8,7,5,0.9)_100%)]"
                          aria-hidden="true"
                        />
                      </>
                    )}
                    <div className="pointer-events-none absolute inset-[6px] border border-[#F3E5AB]/10" aria-hidden="true" />
                    <div className="absolute inset-x-0 bottom-0 px-2.5 pb-3.5 pt-2 text-center sm:px-3.5 sm:pb-5">
                      <span className="font-display block text-[13px] leading-snug text-[#F7F4EF] sm:text-base">
                        {p.name}
                      </span>
                      {p.note && (
                        <span className="mt-1 block text-[10px] leading-snug text-[#C8C2B7] sm:text-xs">{p.note}</span>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 7 — RECOGNITION */}
        <section data-testid="usha-recognition-section" className="relative px-5 py-20 sm:px-8 sm:py-28">
          <div className="mx-auto max-w-4xl">
            <SectionHeading number="06" title="Recognition" icon={Award} />
            <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {usha.awards.map((a, i) => (
                <Reveal key={a.title} delay={i * 0.08}>
                  <div className="flex h-full flex-col rounded-3xl border border-[#D4AF37]/20 bg-[#161310] p-7">
                    <span className="font-display gold-text text-2xl">{a.year}</span>
                    <h3 className="font-display mt-2 text-lg leading-snug text-[#F7F4EF] sm:text-xl">{a.title}</h3>
                    {a.detail && <p className="mt-2 text-sm leading-relaxed text-[#918A7E]">{a.detail}</p>}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 8 — WHY THIS EVENING MATTERS */}
        <section data-testid="usha-connection-section" className="relative overflow-hidden px-5 py-20 sm:px-8 sm:py-28">
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            <div className="absolute left-1/2 top-1/2 h-[500px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#D4AF37]/[0.06] blur-[120px]" />
          </div>
          <div className="relative mx-auto max-w-3xl text-center">
            <Reveal>
              <StarOrnament className="mx-auto h-8 w-8 text-[#D4AF37]/70" />
              <p className="font-accent mt-6 text-2xl italic leading-relaxed text-[#F3E5AB] sm:text-3xl lg:text-4xl">
                {usha.connection}
              </p>
            </Reveal>
          </div>
        </section>

        {/* SECTION 9 — CTA */}
        <section data-testid="usha-final-cta-section" className="relative px-5 pb-24 sm:px-8">
          <Reveal>
            <div className="mx-auto flex max-w-3xl flex-col items-center gap-6 rounded-3xl border border-[#D4AF37]/30 bg-[#0E0C0A] px-8 py-12 text-center">
              <h2 className="font-display text-2xl text-[#F7F4EF] sm:text-3xl">
                Be there when she takes the stage
              </h2>
              <p className="max-w-xl text-sm leading-relaxed text-[#918A7E] sm:text-base">
                {SITE.date} · {SITE.venue.name}, {SITE.venue.city}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4">
                <BookButton location="usha_final_cta" testId="usha-final-book-tickets-btn" />
                <AskChittiButton location="usha_final_cta" testId="usha-final-ask-chitti-btn" />
              </div>
            </div>
          </Reveal>
        </section>
      </main>

      <footer className="border-t border-[#D4AF37]/15 px-5 py-8 text-center text-sm text-[#918A7E]">
        © {new Date().getFullYear()} {SITE.eventName} · Event by {SITE.organizer} ·{" "}
        <a
          href={`mailto:${SITE.contactEmail}`}
          className="text-[#F3E5AB] underline decoration-[#D4AF37]/40 underline-offset-4 hover:text-[#E6CA65]"
        >
          {SITE.contactEmail}
        </a>
      </footer>
      <StickyCta />
    </div>
  );
}
